import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ButtonDeprecated, CallOut, Checkbox, MintTile, YatV2Loader } from '@pkg/components';
import { useAppDispatch, useAppSelector, useMetaMask, useMobile } from '@pkg/hooks';

import { ArtifactMintSteps, type ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { getTranslationByArtifactKey } from '@app/helpers';
import { setArtifactMintPanelStep } from '@app/store/actions/artifacts';
import { mintArtifact } from '@app/store/actions/minting';

import { Typography } from '../../../DesignSystem';
import '../ArtifactMintPanel.scss';

export const AcceptTerms = () => {
    const { t } = useTranslation(['artifacts', 'nft']);
    const dispatch = useAppDispatch();
    const { isMobile } = useMobile();

    const { userHasEnoughFunds } = useMetaMask();
    const { selectedArtifact } = useAppSelector((state) => state.artifacts);

    const [warningAcknowledged, setWarningAcknowledged] = useState(false);
    const [artifactType, setArtifactType] = useState<ArtifactType>();

    useEffect(() => {
        if (selectedArtifact && selectedArtifact.artifact_type) {
            setArtifactType(selectedArtifact.artifact_type);
        } else {
            dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Select));
        }
    }, [dispatch, selectedArtifact]);

    const handleMint = useCallback(() => {
        if (!warningAcknowledged) return;
        if (selectedArtifact && artifactType) {
            dispatch(mintArtifact(artifactType, selectedArtifact.artifactId, selectedArtifact.contract_address));
        }
    }, [dispatch, artifactType, selectedArtifact, warningAcknowledged]);

    if (!artifactType) {
        return (
            <div className="yat-nft-sidebar__step yat-nft-sidebar__name-nft">
                <div className="artifact-sidebar__content">
                    <YatV2Loader />
                </div>
            </div>
        );
    }

    const artifactKey = getTranslationByArtifactKey(t, artifactType, 'label') || t('artifact');
    const ctaSize = isMobile ? 'small' : 'large';
    return (
        <div className="yat-artifact-sidebar__step">
            <div className="yat-artifact-sidebar__content">
                <Typography type="headline" size={40} classes={['yat-artifact-sidebar__title']}>
                    {t('fine_print_title')}
                </Typography>
                <Typography type="paragraph" size={16} classes={['yat-nft-sidebar__text']}>
                    <span>{t('mint_explainer_copy')}</span>
                </Typography>

                {selectedArtifact && (
                    <div className={`yat-artifact-sidebar__preview`}>
                        <MintTile artifactType={artifactType} artifactId={selectedArtifact.artifactId} hideButton />
                    </div>
                )}
                <Checkbox
                    checked={warningAcknowledged}
                    onChange={(e) => setWarningAcknowledged(e.target.checked)}
                    id="nft-acknowledgement"
                >
                    {t('checkbox_mint_artifact')}
                </Checkbox>
                <CallOut verticalMargins={20} />
                <ButtonDeprecated
                    classes={['yat-artifact-sidebar__cta']}
                    color="primary"
                    onClick={handleMint}
                    size={ctaSize}
                    disabled={!warningAcknowledged}
                    fluid={isMobile}
                >
                    {t('mint_my_artifact', { artifact: artifactKey })}
                </ButtonDeprecated>
                {userHasEnoughFunds === false && (
                    <p className="yat-nft-sidebar__error-text">{t('nft:not_enough_funds_in_your_wallet')}</p>
                )}
            </div>
        </div>
    );
};
